import { useRef, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { SidebarSearchBox } from './SidebarSearchBox';
import { Loading } from '@framework/ui/atoms/Loading';
import { KataSearchResult } from './KataSearchResult';
import { SearchResult } from './SearchResult';
import { useSidebarSearchAllRecords } from './SearchResult/useSidebarSearchAllRecords';
import { useSidebarSearchKataRecords } from './KataSearchResult/useSidebarSearchKataRecords';
import { useAtom } from 'jotai';
import { sidebarSearchKeywordAtom } from './sidebarSearchKeywordAtom';
import { useActionLogSender } from '@framework/action-log';

type Props = {
  isVisible: boolean;
  onClose: () => void;
  groupId: string;
};

export const SidebarSearchPanel: React.FC<Props> = ({ isVisible, onClose, groupId }: Props) => {
  const searchBoxRef = useRef<HTMLDivElement>(null);
  const actionLogSender = useActionLogSender();

  const { firstLoaded } = useSidebarSearchAllRecords(groupId, isVisible);
  const { firstKataLoaded } = useSidebarSearchKataRecords(isVisible);

  const [searchKeyword, setSearchKeyword] = useAtom(sidebarSearchKeywordAtom);

  const handleSearch = useCallback(
    (keyword: string) => {
      setSearchKeyword(keyword);
      actionLogSender('global:sidebar:search', { keyword });
    },
    [actionLogSender, setSearchKeyword]
  );

  // パネルが表示されたら検索ボックスにフォーカスを当てる
  useEffect(() => {
    if (isVisible && searchBoxRef.current) {
      const inputElement = searchBoxRef.current.querySelector('input');
      if (inputElement) {
        inputElement.focus();
      }
    }
  }, [isVisible]);

  if (!isVisible) return null;

  return (
    <div className="absolute left-full top-0 z-10 h-screen w-80 bg-white shadow-lg">
      <div className="flex items-center justify-between p-3">
        <div className="text-lg font-semibold">検索</div>
        <button
          className="flex size-8 items-center justify-center rounded-full hover:bg-gray-200 focus:outline-none"
          onClick={onClose}
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </div>

      <div className="p-3" ref={searchBoxRef}>
        <SidebarSearchBox onSearch={handleSearch} searchKeyword={searchKeyword} />
      </div>

      <div className="h-[calc(100vh-130px)] overflow-y-auto overflow-x-hidden p-3 text-sm">
        {firstLoaded && firstKataLoaded ? (
          <>
            <SearchResult />
            <KataSearchResult />
          </>
        ) : searchKeyword.length > 0 ? (
          // レコード取得前に検索が開始された場合はローダーを表示
          <Loading />
        ) : null}
      </div>
    </div>
  );
};
